import { PageHeader } from "@bednbody/ui/components/page-header";
import { useMemo, useState } from "react";
import {
  CalendarDotsIcon,
  CheckCircleIcon,
  MoneyIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { isHubScoped } from "@/lib/jwt-claims";
import { useOpsBookings, EMPTY_FILTERS } from "@/features/ops-dashboard/ops-dashboard.hooks";
import { BookingFilters } from "@/features/ops-dashboard/components/BookingFilters";
import { BookingsTable } from "@/features/ops-dashboard/components/BookingsTable";
import { StatCard } from "@/features/ops-dashboard/components/StatCard";

/**
 * The full booking management view: overview stats, search + filters,
 * complete table. The Ops Dashboard shows only a compact recent-bookings
 * list and links here ("View all") so the dashboard itself stays one screen
 * tall.
 */
export function BookingsPage() {
  const { claims } = useAuth();
  const hubScoped = claims ? isHubScoped(claims.role) : false;
  const hubId = claims?.hub_id ?? null;

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const scopedFilters = useMemo(
    () => (hubId ? { ...filters, hubId: filters.hubId || hubId } : filters),
    [filters, hubId],
  );

  const { data: bookings, isLoading } = useOpsBookings(scopedFilters);
  const { data: allBookings } = useOpsBookings({ ...EMPTY_FILTERS, hubId });

  const stats = useMemo(() => {
    const rows = allBookings ?? [];
    const completed = rows.filter((b) => b.status === "completed");
    const cancelled = rows.filter((b) => b.status === "cancelled");
    // `null` for a hub manager (0285) — the tile is dropped rather than shown
    // as ₱0, which would read as "no takings today".
    const revenue = completed.reduce<number | null>(
      (sum, b) => (sum === null || b.amount_due === null ? null : sum + b.amount_due),
      0,
    );
    return {
      total: rows.length,
      completed: completed.length,
      cancelled: cancelled.length,
      revenue,
    };
  }, [allBookings]);

  return (
    <div className="min-w-0 space-y-8">
      <PageHeader
        description={hubScoped ? "Every booking at your hub." : "Bookings across all hubs."}
        title="Bookings"
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Total bookings" value={stats.total} icon={CalendarDotsIcon} />
        <StatCard label="Completed" value={stats.completed} icon={CheckCircleIcon} tone="success" />
        <StatCard
          label="Cancelled"
          value={stats.cancelled}
          icon={WarningCircleIcon}
          tone="warning"
        />
        {/* Dropped entirely for a hub manager (0285): they have no grant on
            the amount, and a ₱0 tile would read as "no takings". */}
        {stats.revenue !== null ? (
          <StatCard label="Completed revenue" value={stats.revenue} icon={MoneyIcon} prefix="₱" />
        ) : null}
      </div>

      <BookingsTable
        bookings={bookings ?? []}
        isLoading={isLoading}
        header={
          <div className="flex flex-wrap items-center gap-3">
            {/* <p className="mr-auto text-sm font-semibold text-foreground">Booking filters</p> */}
            <BookingFilters
              filters={filters}
              onChange={setFilters}
              showHubPicker={!hubScoped}
              showStatus={false}
            />
          </div>
        }
      />
    </div>
  );
}
